import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Observable } from 'rxjs';
import * as _ from 'lodash';

import { columns } from './columns';
import { ColumnPreset } from '../column-preset.interface';

import { FrontendSearchWidgetComponent } from 'shared/search-widget/frontend-search-widget.component';
import { HiddenScrollComponent } from 'shared/scroll/hidden-scroll/hidden-scroll.component';

import { BrandService } from 'shared/brand/brand.service';


@Component({
  moduleId: module.id,
  selector: 'column-builder',
  templateUrl: 'column-builder.component.html',
  styleUrls: ['column-builder.component.scss'],
})
export class ColumnBuilderComponent extends FrontendSearchWidgetComponent {

  @Input() activeColumns: Array<any> = [];
  @Input() columnsData: Array<any> = [];
  @Input() maxColumns: number = 10;
  @Input() initialColumnsOrder: Array<string>;
  @Input() notRemovableColumns: Array<string> = [];
  @Input() hasColumnsLimit: boolean = true; // if false then dont check maxColumns
  @Input() groupedColumns: Array<any> = []; // one column may contain several columns
  @Input() presets: Array<ColumnPreset> = [];

  @ViewChild(HiddenScrollComponent, {static: false})
  hiddenScroll: HiddenScrollComponent | void;

  columns: any = columns;
  sortingColumns: any = [];
  searchFilterField: string = 'title';
  ghostValue: string = 'column-builder';
  selectedPreset: ColumnPreset;

  get selectedIds(): Array<any> {
    return _.map(this.selected, (item: any) => item.option);
  }

  get columnsDisabled(): boolean {
    if (!this.hasColumnsLimit) { return false; }
    return this.selectedData.length >= this.maxColumns;
  }

  setColumns(): void {
    this.sortingColumns = _.filter(this.columns, (item: any) => item.col !== 'select-btn');
    // order by initial order
    const data = this.getOrderedColumns();
    // set selected checkboxes
    this.data = _.map((data), (item: any) => {
      item.selected = _.some(this.activeColumns, ['columnName', item.option]);
      const groupedCol = _.find(this.groupedColumns, ['option', item.option]);
      // column is selected if active columns have grouped attributes
      if (groupedCol) {
        item.selected = _.some(this.activeColumns, (activeCol: any) => {
          return _.includes(groupedCol.attributes, activeCol.columnName);
        });
      }

      if (!this.isRemovable(item)) {
        item.uncheckable = true;
        item.selected = true;
      }
      return item;
    });
    // set drag'n'dropable selected data
    const selectedData = _.map(this.activeColumns.slice(1), (item: any) => {
      return this.getColumn(item.columnName || item.col);
    });
    this.selectedData = this.combineColumnsInGroup(selectedData);
    this.addMissingNotRemovableColumns();
    _.defer(() => {
      if (this.hiddenScroll instanceof HiddenScrollComponent) {
        this.hiddenScroll.defineContentWidth();
      }
    });
  }

  isRemovable(item: any): boolean {
    return !_.includes(this.notRemovableColumns, item.option);
  }

  removeSelectedItem(item: any) {
    _.remove(this.selectedData, ['option', item.option]);
  }

  resetData(): void {
    this.selectedData = [];
    this.data = [];
    this.sortingColumns = [];
    this.selectedPreset = null;
    super.resetData();
  }

  onDrop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.selectedData, event.previousIndex, event.currentIndex);
  }

  // handlers
  onRemoveColumnClick(item: any) {
    this.removeColumn(item);
    if (!this.columnsDisabled) {
      this.deactivateColumns(false);
    }
    if (this.selectedPreset) {
      this.selectedPreset = null;
    }
  }

  onCheckboxChange(item: any) {
    super.onCheckboxChange(item);
    if (this.columnsDisabled) {
      this.deactivateColumns(true);
    }
    if (this.selectedPreset) {
      this.selectedPreset = null;
    }
  }

  onPresetSelect(checked: boolean, preset: ColumnPreset) {
    if (checked) {
      this.selectedPreset = preset;
      // remove all selected columns
      this.selectedAll = false;
      _.each(_.clone(this.selectedData), (item: any) => {
        this.removeColumn(item);
      });
      // select columns in the preset
      _.each(preset.columnNames, (columnName: string) => {
        const column = _.find(this.data, ['option', columnName]);
        if (!column) { return console.warn('Column not found!'); }
        column.selected = true;
        this.selectedData.push(column);
      });
      // disable columns select if selected columns size > this.maxColumns
      this.deactivateColumns(true);
    } else {
      this.selectedPreset = null;
      // remove all selected columns in the preset
      _.each(preset.columnNames, (columnName: string) => {
        const column = _.find(this.selectedData, ['option', columnName]);
        if (this.isRemovable(column)) {
          this.removeColumn(column);
        }
      });
      // enable other columns select
      this.deactivateColumns(false);
    }
  }

  // private
  private deactivateColumns(active: boolean) {
    this.data.map((item: any) => {
      if (this.isRemovable(item)) {
        item.uncheckable = active;
      }
    });
  }

  private getOrderedColumns(): any {
    return _.chain(this.initialColumnsOrder)
      .map((name: any) => {
        const col = _.find(this.columnsData, ['column_name', name]);
        if (col) {
          return col;
        } else {
          // or column may be grouped
          return _.find(this.groupedColumns, ['option', name]);
        }
      })
      .concat(
        // filter initial ordered items
        _.filter(this.columnsData, (item) => {
          return !_.includes(this.initialColumnsOrder, item.column_name);
        }),
      )
      // filter items which are grouped in one column
      .filter((column: any) => {
        return !this.isGroupedCol(_.get(column, 'column_name'));
      })
      .compact()
      .value();
  }

  private getColumn(columnName: any): any {
    const column: any = _.find(this.columnsData, ['column_name', columnName]);
    return _.assign(column, {
      option: columnName,
      columnName,
    });
  }

  private addMissingNotRemovableColumns(): void {
    _.each(this.notRemovableColumns, (columnName: string) => {
      const item = _.find(this.selectedData, ['column_name', columnName]);
      if (item) { return; }
      this.selectedData.push(this.getColumn(columnName));
    });
  }

  private isGroupedCol(columnName: string): boolean {
    return _.some(this.groupedColumns, (item: any) => {
      return _.includes(item.attributes, columnName);
    });
  }

  private findGroupedCol(columnName: string): any {
    return _.find(this.groupedColumns, (item: any) => {
      return _.includes(item.attributes, columnName);
    });
  }

  private combineColumnsInGroup(data: Array<any>): Array<any> {
    const addedGroupedColumns: any = {};
    return _.chain(data)
    .map((item: any) => {
      if (this.isGroupedCol(item.option)) {
        const column = this.findGroupedCol(item.columnName);
        if (!addedGroupedColumns[column.option]) {
          // replace column on group
          addedGroupedColumns[column.option] = true;
          return column;
        }
        // do not replace column if group had been already added
      } else {
        // return column if it doesn't have group
        return item;
      }
    }).compact().value();
  }

  private removeColumn(item: any): void {
    const column = _.find(this.data, ['option', _.get(item, 'option')]);
    if (!column) { return console.warn('Column not found!'); }
    column.selected = false;
    this.removeSelectedItem(item);
  }
}
